/**
 * platform.js
 *
 * Central runtime detection for whether the app is executing inside the
 * Capacitor native shell (iOS / Android) or a plain web browser. Every
 * platform abstraction under `src/platform/*` calls into `isNative()` /
 * `platform()` before touching a Capacitor plugin so a browser build
 * never throws when a plugin is unavailable.
 *
 * Do NOT import Capacitor plugins here. This module must stay side-
 * effect free so it can be imported from the top of the module graph
 * (e.g. main.jsx) without pulling native code into every web bundle.
 */
import { Capacitor } from '@capacitor/core';

/**
 * True when running inside Capacitor's native runtime (iOS or Android).
 * Returns false in every browser context (dev server, production web
 * build, jsdom tests) — Capacitor stubs `isNativePlatform()` to `false`
 * off-device.
 */
export function isNative() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Canonical platform identifier. Returns `'ios'`, `'android'`, or
 * `'web'`. Never throws.
 */
export function platform() {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
}

export function isIOS() {
  return platform() === 'ios';
}

export function isAndroid() {
  return platform() === 'android';
}

export function isWeb() {
  return platform() === 'web';
}

/**
 * Check whether a specific Capacitor plugin is present in the current
 * runtime. Used before calling plugins whose absence is legitimate
 * (e.g. `PushNotifications` on a simulator, `BarcodeScanner` on the
 * web fallback). Never throws.
 */
export function isPluginAvailable(name) {
  try {
    return typeof name === 'string' && Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
}
